import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import {
  bulkUpdatePricing,
  exportPricingMatrixCsv,
  getPricingMatrix,
  importPricingMatrixCsv,
  type BulkPricingChange,
  type PricingMatrixCell,
  type PricingMatrixData,
} from '@/lib/api/pricing-matrix.js';
import { listRepairServices } from '@/lib/api/repair-services.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges.js';
import { useAuthStore } from '@/stores/auth.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

const DEVICE_CATEGORIES = [
  { value: 'phone', label: 'Mobile Device' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'laptop', label: 'Laptop' },
  { value: 'console', label: 'Console' },
  { value: 'watch', label: 'Wearable' },
  { value: 'other', label: 'Other' },
] as const;

type PendingChange = {
  type: 'update' | 'create';
  modelId: string;
  modelName: string;
  serviceId: string;
  serviceName: string;
  makeId: string;
  oldPrice: number | null;
  newPrice: number;
  pricingRowId: string | null;
};

type CellKey = string;
const cellKey = (modelId: string, serviceId: string): CellKey => `${modelId}:${serviceId}`;

function money(n: number) {
  return n.toFixed(2);
}

export function PricingMatrixPage() {
  const { can } = usePermissions();
  const userRole = useAuthStore((s) => s.user?.role ?? 'read_only');
  const queryClient = useQueryClient();
  const canView = can('pricing_matrix_view') || userRole === 'platform_admin';
  const canEdit = can('pricing_matrix_edit') || userRole === 'platform_admin';

  const [category, setCategory] = useState('');
  const [makeId, setMakeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [loadedParams, setLoadedParams] = useState<{
    category: string;
    makeId?: string;
    modelId?: string;
    serviceIds?: string;
  } | null>(null);

  const [pendingChanges, setPendingChanges] = useState<Map<CellKey, PendingChange>>(new Map());
  const [editingCell, setEditingCell] = useState<CellKey | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState(false);
  const [highlightMissing, setHighlightMissing] = useState(false);
  const [missingJumpIndex, setMissingJumpIndex] = useState(0);
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importPreview, setImportPreview] = useState<Awaited<
    ReturnType<typeof importPricingMatrixCsv>
  > | null>(null);
  const [extraServiceIds, setExtraServiceIds] = useState<string[]>([]);
  const [showAddService, setShowAddService] = useState(false);
  const [addServiceSearch, setAddServiceSearch] = useState('');
  const [colSetPrice, setColSetPrice] = useState('');
  const [colAdjustAmt, setColAdjustAmt] = useState('10');
  const [colAdjustMode, setColAdjustMode] = useState<'pct' | 'flat'>('pct');
  const [colAdjustSign, setColAdjustSign] = useState<1 | -1>(1);
  const [rowServiceId, setRowServiceId] = useState('');
  const [rowSetPrice, setRowSetPrice] = useState('');
  const [rowAdjustAmt, setRowAdjustAmt] = useState('10');
  const [rowAdjustMode, setRowAdjustMode] = useState<'pct' | 'flat'>('pct');
  const [rowAdjustSign, setRowAdjustSign] = useState<1 | -1>(1);
  const [copyFromModelId, setCopyFromModelId] = useState('');
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  useUnsavedChanges(pendingChanges.size > 0);

  const makesQuery = useQuery({
    queryKey: ['device-makes'],
    queryFn: listDeviceMakes,
  });

  const modelsFilterQuery = useQuery({
    queryKey: ['device-models-filter', makeId, category],
    queryFn: () => {
      const params: { makeId?: string; category?: string } = {};
      if (makeId) params.makeId = makeId;
      if (category) params.category = category;
      return listDeviceModels(params);
    },
    enabled: Boolean(category),
  });

  const allServicesQuery = useQuery({
    queryKey: ['repair-services-all'],
    queryFn: () => listRepairServices({ includeInactive: false }),
  });

  const matrixQuery = useQuery({
    queryKey: ['pricing-matrix', loadedParams],
    queryFn: () => getPricingMatrix(loadedParams!),
    enabled: Boolean(loadedParams),
  });

  const matrix: PricingMatrixData | undefined = matrixQuery.data;

  const displayServices = useMemo(() => {
    if (!matrix) return [];
    const base = matrix.services;
    const extras = (allServicesQuery.data ?? [])
      .filter((s) => extraServiceIds.includes(s.id) && !base.some((b) => b.id === s.id))
      .map((s) => ({ id: s.id, name: s.name, categorySlug: s.categorySlug ?? null }));
    return [...base, ...extras];
  }, [matrix, extraServiceIds, allServicesQuery.data]);

  const cellMap = useMemo(() => {
    const map = new Map<CellKey, PricingMatrixCell>();
    for (const cell of matrix?.pricing ?? []) {
      map.set(cellKey(cell.modelId, cell.serviceId), cell);
    }
    return map;
  }, [matrix]);

  const getEffectivePrice = useCallback(
    (modelIdVal: string, serviceIdVal: string): { price: number | null; hasRow: boolean; pricingRowId: string | null } => {
      const key = cellKey(modelIdVal, serviceIdVal);
      const pending = pendingChanges.get(key);
      if (pending) {
        return { price: pending.newPrice, hasRow: true, pricingRowId: pending.pricingRowId };
      }
      const cell = cellMap.get(key);
      if (!cell || cell.pricingRowId == null) {
        return { price: null, hasRow: false, pricingRowId: null };
      }
      return {
        price: Number(cell.price ?? 0),
        hasRow: true,
        pricingRowId: cell.pricingRowId,
      };
    },
    [pendingChanges, cellMap],
  );

  const missingCells = useMemo(() => {
    if (!matrix) return [] as Array<{ modelId: string; serviceId: string }>;
    const out: Array<{ modelId: string; serviceId: string }> = [];
    for (const model of matrix.models) {
      for (const service of displayServices) {
        const eff = getEffectivePrice(model.id, service.id);
        if (!eff.hasRow) out.push({ modelId: model.id, serviceId: service.id });
      }
    }
    return out;
  }, [matrix, displayServices, getEffectivePrice]);

  function loadMatrix() {
    if (!category) {
      toastError('Select a category first');
      return;
    }
    if (pendingChanges.size > 0) {
      if (!window.confirm('You have unsaved changes. Load a new matrix anyway?')) return;
      setPendingChanges(new Map());
    }
    setExtraServiceIds([]);
    setSelectedRows(new Set());
    setSelectedColumn(null);
    setLoadedParams({
      category,
      ...(makeId ? { makeId } : {}),
      ...(modelId ? { modelId } : {}),
      ...(selectedServiceIds.length ? { serviceIds: selectedServiceIds.join(',') } : {}),
    });
  }

  function setPending(
    model: { id: string; name: string; makeId: string },
    service: { id: string; name: string },
    newPrice: number,
    pricingRowId: string | null,
    oldPrice: number | null,
  ) {
    const key = cellKey(model.id, service.id);
    const original = cellMap.get(key);
    const originalPrice =
      original?.pricingRowId != null ? Number(original.price ?? 0) : null;
    const type: 'update' | 'create' = pricingRowId || original?.pricingRowId ? 'update' : 'create';
    const rowId = pricingRowId ?? original?.pricingRowId ?? null;

    if (originalPrice != null && Math.abs(originalPrice - newPrice) < 0.001 && type === 'update') {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      return;
    }

    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(key, {
        type: rowId ? 'update' : 'create',
        modelId: model.id,
        modelName: model.name,
        serviceId: service.id,
        serviceName: service.name,
        makeId: model.makeId,
        oldPrice: oldPrice ?? originalPrice,
        newPrice,
        pricingRowId: rowId,
      });
      return next;
    });
  }

  function startEdit(modelIdVal: string, serviceIdVal: string, initial: string) {
    if (!canEdit) return;
    setEditingCell(cellKey(modelIdVal, serviceIdVal));
    setEditValue(initial);
    setEditError(false);
  }

  function commitEdit(modelIdVal: string, serviceIdVal: string) {
    if (!matrix) return;
    const model = matrix.models.find((m) => m.id === modelIdVal);
    const service = displayServices.find((s) => s.id === serviceIdVal);
    if (!model || !service) return;

    const parsed = Number(editValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setEditError(true);
      return;
    }

    const eff = getEffectivePrice(modelIdVal, serviceIdVal);
    setPending(model, service, parsed, eff.pricingRowId, eff.hasRow ? eff.price : null);
    setEditingCell(null);
    setEditError(false);
  }

  function addMissingCell(modelIdVal: string, serviceIdVal: string) {
    if (!matrix || !canEdit) return;
    const model = matrix.models.find((m) => m.id === modelIdVal);
    const service = displayServices.find((s) => s.id === serviceIdVal);
    if (!model || !service) return;
    setPending(model, service, 0, null, null);
    startEdit(modelIdVal, serviceIdVal, '0.00');
  }

  function navigateCell(modelIdx: number, serviceIdx: number, dir: 'right' | 'left' | 'down' | 'up') {
    if (!matrix) return;
    let m = modelIdx;
    let s = serviceIdx;
    if (dir === 'right') s += 1;
    if (dir === 'left') s -= 1;
    if (dir === 'down') m += 1;
    if (dir === 'up') m -= 1;
    if (m < 0 || m >= matrix.models.length || s < 0 || s >= displayServices.length) return;
    const model = matrix.models[m]!;
    const service = displayServices[s]!;
    const eff = getEffectivePrice(model.id, service.id);
    if (!eff.hasRow) {
      addMissingCell(model.id, service.id);
    } else {
      startEdit(model.id, service.id, money(eff.price ?? 0));
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const changes: BulkPricingChange[] = [...pendingChanges.values()].map((c) => {
        const change: BulkPricingChange = {
          type: c.type,
          serviceId: c.serviceId,
          modelId: c.modelId,
          makeId: c.makeId,
          price: c.newPrice,
        };
        if (c.pricingRowId) change.pricingRowId = c.pricingRowId;
        return change;
      });
      return bulkUpdatePricing(changes);
    },
    onSuccess: (data) => {
      const total = data.updated + data.created;
      toastSuccess(`${total} prices saved ✓`);
      setPendingChanges(new Map());
      setShowSaveModal(false);
      queryClient.invalidateQueries({ queryKey: ['pricing-matrix'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  function applyColumnSetAll() {
    if (!matrix || !selectedColumn || !canEdit) return;
    const price = Number(colSetPrice);
    if (!Number.isFinite(price) || price < 0) {
      toastError('Enter a valid price');
      return;
    }
    const service = displayServices.find((s) => s.id === selectedColumn);
    if (!service) return;
    for (const model of matrix.models) {
      const eff = getEffectivePrice(model.id, service.id);
      setPending(model, service, price, eff.pricingRowId, eff.hasRow ? eff.price : null);
    }
  }

  function applyColumnAdjust() {
    if (!matrix || !selectedColumn || !canEdit) return;
    const amt = Number(colAdjustAmt);
    if (!Number.isFinite(amt)) return;
    const service = displayServices.find((s) => s.id === selectedColumn);
    if (!service) return;
    for (const model of matrix.models) {
      const eff = getEffectivePrice(model.id, service.id);
      if (!eff.hasRow || eff.price == null) continue;
      const next =
        colAdjustMode === 'pct'
          ? Math.max(0, eff.price * (1 + (colAdjustSign * amt) / 100))
          : Math.max(0, eff.price + colAdjustSign * amt);
      setPending(model, service, Number(next.toFixed(2)), eff.pricingRowId, eff.price);
    }
  }

  function applyRowSet() {
    if (!matrix || !rowServiceId || !canEdit) return;
    const price = Number(rowSetPrice);
    if (!Number.isFinite(price) || price < 0) return;
    const service = displayServices.find((s) => s.id === rowServiceId);
    if (!service) return;
    for (const modelIdSel of selectedRows) {
      const model = matrix.models.find((m) => m.id === modelIdSel);
      if (!model) continue;
      const eff = getEffectivePrice(model.id, service.id);
      setPending(model, service, price, eff.pricingRowId, eff.hasRow ? eff.price : null);
    }
  }

  function applyRowAdjust() {
    if (!matrix || !canEdit) return;
    const amt = Number(rowAdjustAmt);
    if (!Number.isFinite(amt)) return;
    for (const modelIdSel of selectedRows) {
      const model = matrix.models.find((m) => m.id === modelIdSel);
      if (!model) continue;
      for (const service of displayServices) {
        const eff = getEffectivePrice(model.id, service.id);
        if (!eff.hasRow || eff.price == null) continue;
        const next =
          rowAdjustMode === 'pct'
            ? Math.max(0, eff.price * (1 + (rowAdjustSign * amt) / 100))
            : Math.max(0, eff.price + rowAdjustSign * amt);
        setPending(model, service, Number(next.toFixed(2)), eff.pricingRowId, eff.price);
      }
    }
  }

  function confirmCopyFrom() {
    if (!matrix || !copyFromModelId || !canEdit) return;
    const source = matrix.models.find((m) => m.id === copyFromModelId);
    if (!source) return;
    for (const modelIdSel of selectedRows) {
      if (modelIdSel === copyFromModelId) continue;
      const model = matrix.models.find((m) => m.id === modelIdSel);
      if (!model) continue;
      for (const service of displayServices) {
        const src = getEffectivePrice(source.id, service.id);
        if (!src.hasRow || src.price == null) continue;
        const dest = getEffectivePrice(model.id, service.id);
        setPending(model, service, src.price, dest.pricingRowId, dest.hasRow ? dest.price : null);
      }
    }
    setShowCopyConfirm(false);
    toastSuccess('Prices copied to selected models');
  }

  async function handleExport() {
    if (!loadedParams) return;
    try {
      const blob = await exportPricingMatrixCsv(loadedParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pricing-matrix-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toastError('Export failed');
    }
  }

  async function handleImportFile(file: File) {
    const text = await file.text();
    try {
      const preview = await importPricingMatrixCsv(text);
      setImportPreview(preview);
      setShowImportPreview(true);
    } catch (error) {
      toastError(parseApiError(error).message);
    }
  }

  const importConfirmMutation = useMutation({
    mutationFn: async () => {
      if (!importPreview) return;
      const changes: BulkPricingChange[] = importPreview.preview
        .filter((p) => p.action !== 'skip' && p.modelId && p.serviceId)
        .map((p) => {
          const change: BulkPricingChange = {
            type: p.action as 'update' | 'create',
            serviceId: p.serviceId!,
            modelId: p.modelId!,
            price: Number(p.newPrice),
          };
          if (p.pricingRowId) change.pricingRowId = p.pricingRowId;
          if (p.makeId) change.makeId = p.makeId;
          return change;
        });
      return bulkUpdatePricing(changes);
    },
    onSuccess: (data) => {
      if (!data) return;
      toastSuccess(`${data.updated + data.created} prices imported ✓`);
      setShowImportPreview(false);
      setImportPreview(null);
      queryClient.invalidateQueries({ queryKey: ['pricing-matrix'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  function jumpToMissing() {
    if (!missingCells.length || !matrix) return;
    const idx = missingJumpIndex % missingCells.length;
    const cell = missingCells[idx]!;
    setMissingJumpIndex(idx + 1);
    const el = document.querySelector(
      `[data-cell="${cell.modelId}:${cell.serviceId}"]`,
    ) as HTMLElement | null;
    el?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    el?.classList.add('ring-2', 'ring-amber-400');
    setTimeout(() => el?.classList.remove('ring-2', 'ring-amber-400'), 1200);
  }

  if (!canView) {
    return <Navigate to="/settings/repair-services" replace />;
  }

  const pendingList = [...pendingChanges.values()];
  const updates = pendingList.filter((c) => c.type === 'update');
  const creates = pendingList.filter((c) => c.type === 'create');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-950">Pricing Matrix</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage repair service pricing across all devices
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pendingChanges.size > 0 ? (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
              {pendingChanges.size} unsaved changes
            </span>
          ) : null}
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canEdit}
          >
            📥 Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleImportFile(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold disabled:opacity-50"
            onClick={() => void handleExport()}
            disabled={!loadedParams}
          >
            📤 Export CSV
          </button>
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={!canEdit || pendingChanges.size === 0}
            onClick={() => setShowSaveModal(true)}
          >
            💾 Save {pendingChanges.size > 0 ? `(${pendingChanges.size} changes)` : 'Changes'}
          </button>
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label className="block text-sm font-medium">
            Category *
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setMakeId('');
                setModelId('');
              }}
            >
              <option value="">Select category</option>
              {DEVICE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium">
            Brand
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={makeId}
              disabled={!category}
              onChange={(e) => {
                setMakeId(e.target.value);
                setModelId('');
              }}
            >
              <option value="">All Brands</option>
              {(makesQuery.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium">
            Model
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={modelId}
              disabled={!category}
              onChange={(e) => setModelId(e.target.value)}
            >
              <option value="">All Models</option>
              {(modelsFilterQuery.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium md:col-span-2 xl:col-span-1">
            Services
            <select
              multiple
              className="mt-1 h-24 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={selectedServiceIds}
              onChange={(e) =>
                setSelectedServiceIds(
                  Array.from(e.target.selectedOptions).map((o) => o.value),
                )
              }
            >
              {(allServicesQuery.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              Hold Ctrl/Cmd to multi-select. Empty = all services.
            </span>
          </label>
          <div className="flex items-end">
            <button
              type="button"
              className="w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!category}
              onClick={loadMatrix}
            >
              Load Pricing Matrix
            </button>
          </div>
        </div>
      </section>

      {matrix ? (
        <>
          <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
            <span>
              Showing: <strong>{matrix.models.length}</strong> models ×{' '}
              <strong>{displayServices.length}</strong> services ={' '}
              <strong>{matrix.models.length * displayServices.length}</strong> cells
            </span>
            <span className="text-amber-700">
              Missing prices: {missingCells.length} ⚠
            </span>
            <label className="flex items-center gap-2 font-medium">
              <input
                type="checkbox"
                checked={highlightMissing}
                onChange={(e) => setHighlightMissing(e.target.checked)}
              />
              Highlight Missing
            </label>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold disabled:opacity-50"
              disabled={!missingCells.length}
              onClick={jumpToMissing}
            >
              Jump to Missing
            </button>
          </div>

          {selectedColumn && canEdit ? (
            <div className="rounded-2xl border border-primary/30 bg-blue-50 p-4 text-sm">
              <p className="font-semibold text-slate-900">
                {displayServices.find((s) => s.id === selectedColumn)?.name} —{' '}
                {matrix.models.length} models selected
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label>
                  Set all to: $
                  <input
                    className="ml-1 w-24 rounded-lg border border-slate-300 px-2 py-1"
                    value={colSetPrice}
                    onChange={(e) => setColSetPrice(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="rounded-lg bg-primary px-3 py-1.5 font-semibold text-white"
                  onClick={applyColumnSetAll}
                >
                  Apply
                </button>
                <label className="flex items-center gap-1">
                  Adjust by:
                  <button
                    type="button"
                    className="rounded border px-2"
                    onClick={() => setColAdjustSign((s) => (s === 1 ? -1 : 1))}
                  >
                    {colAdjustSign === 1 ? '+/-' : '−/+'}
                  </button>
                  <input
                    className="w-16 rounded-lg border border-slate-300 px-2 py-1"
                    value={colAdjustAmt}
                    onChange={(e) => setColAdjustAmt(e.target.value)}
                  />
                  <select
                    className="rounded-lg border border-slate-300 px-2 py-1"
                    value={colAdjustMode}
                    onChange={(e) => setColAdjustMode(e.target.value as 'pct' | 'flat')}
                  >
                    <option value="pct">%</option>
                    <option value="flat">$</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold"
                  onClick={applyColumnAdjust}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold"
                  onClick={() => setSelectedColumn(null)}
                >
                  Deselect Column
                </button>
              </div>
            </div>
          ) : null}

          {selectedRows.size > 0 && canEdit ? (
            <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 text-sm">
              <p className="font-semibold">{selectedRows.size} models selected</p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label>
                  Set column:{' '}
                  <select
                    className="rounded-lg border border-slate-300 px-2 py-1"
                    value={rowServiceId}
                    onChange={(e) => setRowServiceId(e.target.value)}
                  >
                    <option value="">Service…</option>
                    {displayServices.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>{' '}
                  to: $
                  <input
                    className="ml-1 w-20 rounded-lg border border-slate-300 px-2 py-1"
                    value={rowSetPrice}
                    onChange={(e) => setRowSetPrice(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="rounded-lg bg-emerald-700 px-3 py-1.5 font-semibold text-white"
                  onClick={applyRowSet}
                >
                  Apply
                </button>
                <label className="flex items-center gap-1">
                  Adjust all prices by:
                  <button
                    type="button"
                    className="rounded border px-2"
                    onClick={() => setRowAdjustSign((s) => (s === 1 ? -1 : 1))}
                  >
                    {rowAdjustSign === 1 ? '+/-' : '−/+'}
                  </button>
                  <input
                    className="w-16 rounded-lg border border-slate-300 px-2 py-1"
                    value={rowAdjustAmt}
                    onChange={(e) => setRowAdjustAmt(e.target.value)}
                  />
                  <select
                    className="rounded-lg border border-slate-300 px-2 py-1"
                    value={rowAdjustMode}
                    onChange={(e) => setRowAdjustMode(e.target.value as 'pct' | 'flat')}
                  >
                    <option value="pct">%</option>
                    <option value="flat">$</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold"
                  onClick={applyRowAdjust}
                >
                  Apply
                </button>
                <label>
                  Copy from:{' '}
                  <select
                    className="rounded-lg border border-slate-300 px-2 py-1"
                    value={copyFromModelId}
                    onChange={(e) => setCopyFromModelId(e.target.value)}
                  >
                    <option value="">Model…</option>
                    {matrix.models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold disabled:opacity-50"
                  disabled={!copyFromModelId}
                  onClick={() => setShowCopyConfirm(true)}
                >
                  Copy All
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold"
                  onClick={() => setSelectedRows(new Set())}
                >
                  Deselect All
                </button>
              </div>
            </div>
          ) : null}

          <div
            ref={tableRef}
            className="max-h-[70vh] overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm"
          >
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-30 min-w-[200px] border-b border-r border-slate-200 bg-slate-100 px-3 py-2 text-left">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={
                          matrix.models.length > 0 &&
                          selectedRows.size === matrix.models.length
                        }
                        onChange={(e) => {
                          setSelectedRows(
                            e.target.checked
                              ? new Set(matrix.models.map((m) => m.id))
                              : new Set(),
                          );
                        }}
                      />
                      <span>Model</span>
                    </div>
                  </th>
                  {displayServices.map((service) => (
                    <th
                      key={service.id}
                      className={cn(
                        'sticky top-0 z-20 min-w-[110px] border-b border-slate-200 bg-slate-100 px-2 py-2 text-center cursor-pointer',
                        selectedColumn === service.id && 'bg-blue-100',
                      )}
                      onClick={() =>
                        setSelectedColumn((c) => (c === service.id ? null : service.id))
                      }
                    >
                      <div className="font-semibold leading-tight">{service.name}</div>
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedColumn === service.id}
                        onChange={() =>
                          setSelectedColumn((c) => (c === service.id ? null : service.id))
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                  <th className="sticky top-0 z-20 min-w-[80px] border-b border-slate-200 bg-slate-100 px-2 py-2">
                    <button
                      type="button"
                      className="text-xs font-semibold text-primary"
                      onClick={() => setShowAddService(true)}
                    >
                      + Add
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {matrix.models.map((model, modelIdx) => (
                  <tr
                    key={model.id}
                    className={cn(selectedRows.has(model.id) && 'bg-emerald-50/60')}
                  >
                    <td className="sticky left-0 z-10 border-b border-r border-slate-100 bg-white px-3 py-1.5 font-medium">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(model.id)}
                          onChange={() => {
                            setSelectedRows((prev) => {
                              const next = new Set(prev);
                              if (next.has(model.id)) next.delete(model.id);
                              else next.add(model.id);
                              return next;
                            });
                          }}
                        />
                        <span>
                          <span className="block text-xs text-slate-400">{model.makeName}</span>
                          {model.name}
                        </span>
                      </label>
                    </td>
                    {displayServices.map((service, serviceIdx) => {
                      const key = cellKey(model.id, service.id);
                      const eff = getEffectivePrice(model.id, service.id);
                      const pending = pendingChanges.has(key);
                      const isEditing = editingCell === key;

                      if (isEditing) {
                        return (
                          <td key={service.id} className="border-b border-slate-100 p-1">
                            <input
                              autoFocus
                              type="number"
                              step="0.01"
                              min="0"
                              className={cn(
                                'w-full rounded-lg border-0 bg-amber-50 px-2 py-1.5 text-center outline-none',
                                editError
                                  ? 'shadow-[0_0_0_2px_#EF4444]'
                                  : 'shadow-[0_0_0_2px_#F59E0B]',
                              )}
                              value={editValue}
                              onChange={(e) => {
                                setEditValue(e.target.value);
                                setEditError(false);
                              }}
                              onBlur={() => commitEdit(model.id, service.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setEditingCell(null);
                                  setEditError(false);
                                } else if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitEdit(model.id, service.id);
                                  navigateCell(modelIdx, serviceIdx, 'down');
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  commitEdit(model.id, service.id);
                                  navigateCell(
                                    modelIdx,
                                    serviceIdx,
                                    e.shiftKey ? 'left' : 'right',
                                  );
                                }
                              }}
                            />
                          </td>
                        );
                      }

                      if (!eff.hasRow) {
                        return (
                          <td
                            key={service.id}
                            data-cell={key}
                            className={cn(
                              'border-b border-slate-100 p-1 text-center',
                              highlightMissing && 'bg-amber-100',
                              selectedColumn === service.id && 'bg-blue-50',
                            )}
                          >
                            <button
                              type="button"
                              className="text-sm font-semibold text-primary disabled:opacity-40"
                              disabled={!canEdit}
                              onClick={() => addMissingCell(model.id, service.id)}
                            >
                              + Add
                            </button>
                          </td>
                        );
                      }

                      const isZero = (eff.price ?? 0) === 0;
                      return (
                        <td
                          key={service.id}
                          data-cell={key}
                          className={cn(
                            'border-b border-slate-100 p-1 text-center',
                            selectedColumn === service.id && 'bg-blue-50',
                          )}
                        >
                          <button
                            type="button"
                            title={pending ? 'Unsaved change' : undefined}
                            className={cn(
                              'w-full rounded-lg px-2 py-1.5 font-medium',
                              pending && 'bg-amber-50 shadow-[0_0_0_2px_#F59E0B]',
                              !pending && !isZero && 'bg-slate-100',
                              !pending && isZero && 'bg-white text-slate-400',
                              !canEdit && 'cursor-default',
                            )}
                            onClick={() =>
                              startEdit(model.id, service.id, money(eff.price ?? 0))
                            }
                            disabled={!canEdit}
                          >
                            ${money(eff.price ?? 0)}
                          </button>
                        </td>
                      );
                    })}
                    <td className="border-b border-slate-100" />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          {matrixQuery.isFetching
            ? 'Loading matrix…'
            : 'Select a category and click Load Pricing Matrix to begin.'}
        </div>
      )}

      {showSaveModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[80vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold">Save {pendingList.length} Price Changes?</h3>
            {updates.length ? (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase text-slate-500">
                  Updates ({updates.length})
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {updates.map((c) => (
                    <li key={`${c.modelId}:${c.serviceId}`}>
                      {c.modelName} · {c.serviceName} ${money(c.oldPrice ?? 0)}→$
                      {money(c.newPrice)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {creates.length ? (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase text-slate-500">
                  New ({creates.length})
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {creates.map((c) => (
                    <li key={`${c.modelId}:${c.serviceId}`}>
                      {c.modelName} · {c.serviceName} [new] ${money(c.newPrice)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 font-semibold"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending
                  ? 'Saving…'
                  : `Save All ${pendingList.length} Changes`}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCopyConfirm && matrix ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold">Copy prices?</h3>
            <p className="mt-2 text-sm text-slate-600">
              Copy {displayServices.length} prices from{' '}
              {matrix.models.find((m) => m.id === copyFromModelId)?.name} to{' '}
              {selectedRows.size} models (
              {displayServices.length * selectedRows.size} changes)
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 font-semibold"
                onClick={() => setShowCopyConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 font-semibold text-white"
                onClick={confirmCopyFrom}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showAddService ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold">Add Service Column</h3>
            <input
              className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2"
              placeholder="Search repair services…"
              value={addServiceSearch}
              onChange={(e) => setAddServiceSearch(e.target.value)}
            />
            <div className="mt-3 max-h-60 space-y-1 overflow-auto">
              {(allServicesQuery.data ?? [])
                .filter(
                  (s) =>
                    !displayServices.some((d) => d.id === s.id) &&
                    s.name.toLowerCase().includes(addServiceSearch.toLowerCase()),
                )
                .map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-100"
                    onClick={() => {
                      setExtraServiceIds((ids) => [...ids, s.id]);
                      setShowAddService(false);
                      setAddServiceSearch('');
                    }}
                  >
                    {s.name}
                  </button>
                ))}
            </div>
            <button
              type="button"
              className="mt-4 rounded-xl border border-slate-300 px-4 py-2 font-semibold"
              onClick={() => setShowAddService(false)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      {showImportPreview && importPreview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold">Import Preview</h3>
            <p className="mt-1 text-sm text-slate-500">
              {importPreview.preview.filter((p) => p.action !== 'skip').length} changes ready
            </p>
            {importPreview.unmatchedModels.length ? (
              <p className="mt-2 text-sm text-amber-700">
                Unmatched models: {importPreview.unmatchedModels.join(', ')}
              </p>
            ) : null}
            {importPreview.unmatchedServices.length ? (
              <p className="mt-1 text-sm text-amber-700">
                Unmatched services: {importPreview.unmatchedServices.join(', ')}
              </p>
            ) : null}
            <ul className="mt-4 max-h-64 space-y-1 overflow-auto text-sm">
              {importPreview.preview
                .filter((p) => p.action !== 'skip')
                .slice(0, 100)
                .map((p, i) => (
                  <li key={i}>
                    {p.modelName} · {p.serviceName}: {p.oldPrice ?? '—'} → {p.newPrice} (
                    {p.action})
                  </li>
                ))}
            </ul>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 font-semibold"
                onClick={() => {
                  setShowImportPreview(false);
                  setImportPreview(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
                disabled={importConfirmMutation.isPending || !canEdit}
                onClick={() => importConfirmMutation.mutate()}
              >
                {importConfirmMutation.isPending ? 'Importing…' : 'Confirm Import'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
